import { Event, EventSchema, Invitation, InvitationSchema, User, UserSchema } from "@garther/interfaces";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { invitationApiClient } from "../libs/api/invitation-client";

interface InvitationState {
  invitations: Invitation[];
  currentInvitation: Invitation | null;
  currentEvent: Event | null;
  currentInviterProfile: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setInvitations: (invitations: Invitation[]) => void;
  setCurrentInvitation: (invitation: Invitation | null) => void;
  setCurrentEvent: (event: Event | null) => void;
  setCurrentInviterProfile: (profile: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // API calls
  createInvitation: (bookingId: string) => Promise<Invitation | null>;
  getInvitationByCode: (invitationCode: string) => Promise<Invitation | null>;
  getInvitationByCodeWithDetails: (invitationCode: string) => Promise<{
    invitation: Invitation | null;
    event: Event | null;
    inviterProfile: User | null;
  }>;
  confirmInvitation: (invitationCode: string) => Promise<Invitation | null>;
  declineInvitation: (invitationCode: string) => Promise<Invitation | null>;
  copyInvitationLink: (invitationCode: string) => Promise<void>;

  // Utilities
  generateInvitationLink: (invitationCode: string) => string;
  reset: () => void;
}

export const useInvitationStore = create<InvitationState>()(
  devtools(
    (set) => ({
      invitations: [],
      currentInvitation: null,
      currentEvent: null,
      currentInviterProfile: null,
      isLoading: false,
      error: null,

      setInvitations: (invitations) => {
        set({ invitations }, false, "setInvitations");
      },

      setCurrentInvitation: (invitation) => {
        set({ currentInvitation: invitation }, false, "setCurrentInvitation");
      },

      setCurrentEvent: (event) => {
        set({ currentEvent: event }, false, "setCurrentEvent");
      },

      setCurrentInviterProfile: (profile) => {
        set({ currentInviterProfile: profile }, false, "setCurrentInviterProfile");
      },

      setLoading: (loading) => {
        set({ isLoading: loading }, false, "setLoading");
      },

      setError: (error) => {
        set({ error }, false, "setError");
      },

      createInvitation: async (bookingId) => {
        set({ isLoading: true, error: null }, false, "createInvitation");

        try {
          const invitation = await invitationApiClient.createInvitation(bookingId);
          set({ isLoading: false }, false, "createInvitationSuccess");
          return invitation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to create invitation";
          set({ error: errorMessage, isLoading: false }, false, "createInvitationError");
          return null;
        }
      },

      getInvitationByCode: async (invitationCode) => {
        set({ isLoading: true, error: null }, false, "getInvitationByCode");

        try {
          const invitation = await invitationApiClient.getInvitationById(invitationCode);
          set({ currentInvitation: invitation, isLoading: false }, false, "getInvitationByCodeSuccess");
          return invitation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to get invitation";
          set({ error: errorMessage, isLoading: false }, false, "getInvitationByCodeError");
          return null;
        }
      },

      getInvitationWithDetails: async (id: string) => {
        set({ isLoading: true, error: null }, false, "getInvitationByCodeWithDetails");

        try {
          // First get the invitation
          const invitation = await invitationApiClient.getInvitationWithDetails(id);
          if (!invitation) {
            set({ isLoading: false }, false, "getInvitationByCodeWithDetailsNotFound");
            return { invitation: null, event: null, inviterProfile: null };
          }

          // Update state
          set(
            {
              currentInvitation: InvitationSchema.parse(invitation),
              currentEvent: EventSchema.parse(invitation.booking.event),
              currentInviterProfile: UserSchema.parse(invitation.booking.user),
              isLoading: false
            },
            false,
            "getInvitationByCodeWithDetailsSuccess"
          );

          return {
            invitation: InvitationSchema.parse(invitation),
            event: EventSchema.parse(invitation.booking.event),
            inviterProfile: UserSchema.parse(invitation.booking.user)
          };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to get invitation details";
          set({ error: errorMessage, isLoading: false }, false, "getInvitationByCodeWithDetailsError");
          return { invitation: null, event: null, inviterProfile: null };
        }
      },

      confirmInvitation: async (invitationCode) => {
        set({ isLoading: true, error: null }, false, "confirmInvitation");

        try {
          const invitation = await invitationApiClient.confirmInvitation(invitationCode);
          set({ currentInvitation: invitation, isLoading: false }, false, "confirmInvitationSuccess");
          return invitation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to confirm invitation";
          set({ error: errorMessage, isLoading: false }, false, "confirmInvitationError");
          return null;
        }
      },

      declineInvitation: async (invitationCode) => {
        set({ isLoading: true, error: null }, false, "declineInvitation");

        try {
          const invitation = await invitationApiClient.declineInvitation(invitationCode);
          set({ currentInvitation: invitation, isLoading: false }, false, "declineInvitationSuccess");
          return invitation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to decline invitation";
          set({ error: errorMessage, isLoading: false }, false, "declineInvitationError");
          return null;
        }
      },

      copyInvitationLink: async (invitationCode) => {
        try {
          await invitationApiClient.copyInvitationLinkToClipboard(invitationCode);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to copy link";
          set({ error: errorMessage }, false, "copyInvitationLinkError");
        }
      },

      generateInvitationLink: (invitationCode) => {
        return invitationApiClient.generateInvitationLink(invitationCode);
      },

      reset: () => {
        set(
          {
            invitations: [],
            currentInvitation: null,
            currentEvent: null,
            currentInviterProfile: null,
            isLoading: false,
            error: null
          },
          false,
          "reset"
        );
      }
    }),
    {
      name: "friend-invitation-store"
    }
  )
);
